import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Home Renovation Electrical Services',
  provider: {
    '@type': 'ElectricalContractor',
    name: 'Electrician Solutions Fresno',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Fresno',
    '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
  },
  description:
    'Professional electrical installation services for home renovations in Fresno CA including kitchen remodels, bathroom upgrades, room additions, basement finishing, whole-home rewiring, panel upgrades, and code compliance by licensed C-10 electricians',
  url: 'https://electriciansolutionsfresno.com/services/residential-electrical/home-renovation-electrical',
};

export default function HomeRenovationElectricalPage() {
  const features = [
    {
      icon: 'ri-home-gear-line',
      title: 'Kitchen Remodel Electrical',
      description:
        'Complete electrical installations for kitchen renovations — new circuits for appliances, under-cabinet lighting, island outlets, pendant lights, and GFCI protection for Fresno homes',
    },
    {
      icon: 'ri-drop-line',
      title: 'Bathroom Remodel Electrical',
      description:
        'Professional bathroom electrical upgrades including GFCI outlets, vanity lighting, exhaust fans, heated floors, towel warmers, and code-compliant wiring for Fresno bathroom renovations',
    },
    {
      icon: 'ri-building-line',
      title: 'Room Addition Electrical',
      description:
        'Complete electrical installations for room additions and home expansions — new circuits, outlets, switches, lighting, and HVAC electrical for Fresno home addition projects',
    },
    {
      icon: 'ri-stairs-line',
      title: 'Basement Finishing Electrical',
      description:
        'Full electrical installations for basement finishing projects — lighting, outlets, switches, dedicated circuits, and code-compliant wiring for Fresno basement conversions',
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'Whole-Home Rewiring',
      description:
        'Complete electrical rewiring during major renovations — replace outdated wiring, upgrade to modern standards, and ensure code compliance for Fresno home remodel projects',
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Panel Upgrades for Renovations',
      description:
        'Electrical panel upgrades to support renovation electrical loads — upgrade from 100A to 200A+ to handle new appliances, HVAC, and electrical demands in Fresno homes',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Lighting Design & Installation',
      description:
        'Custom lighting design and installation for renovations — recessed lights, chandeliers, pendant lights, under-cabinet lighting, and smart lighting systems for Fresno remodels',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Code Compliance & Permits',
      description:
        'Ensure all renovation electrical work meets California Title 24 codes and local Fresno building requirements — permits pulled, inspections scheduled, and full code compliance',
    },
  ];

  const whatsIncluded = [
    'Free renovation electrical consultation and detailed project quote for Fresno properties',
    'Licensed C-10 electricians with extensive home renovation experience',
    'Coordination with contractors, designers, and homeowners throughout renovation projects',
    'Complete electrical plans and layouts meeting California Title 24 building codes',
    'High-quality materials and equipment from trusted brands — no cheap substitutes',
    'Permits pulled and inspections scheduled for all major electrical renovation work',
    'Clean, professional work with minimal disruption to your home and renovation timeline',
    'Warranty on all labor and materials — your satisfaction and safety are guaranteed',
  ];

  return (
    <>
      <SEOHead
        title="Home Renovation Electrical Services Fresno CA | Remodel Electrician"
        description="Professional electrical services for home renovations in Fresno, CA. Kitchen remodels, bathroom upgrades, room additions, basement finishing, rewiring, panel upgrades. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical/home-renovation-electrical"
        keywords="home renovation electrician Fresno, remodel electrical services Fresno CA, kitchen remodel electrician Fresno, room addition electrical Fresno"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Home Renovation Electrical — Fresno, CA"
        heroSubtitle="Complete electrical services for your home remodel — kitchens, bathrooms, additions & more"
        heroImage="https://public.readdy.ai/ai/img_res/c28177a209ec41605d7bd38a298fe76d.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20new%20electrical%20outlets%20and%20wiring%20during%20kitchen%20remodel%20with%20modern%20cabinets%20and%20countertops%20visible%20tools%20and%20equipment%20bright%20indoor%20lighting%20California%20home%20renovation&width=700&height=600&seq=reno-fresno-kitchen-04&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Powering Fresno home renovations with professional electrical installation services that meet California building codes. Whether you're remodeling a kitchen in a Tower District home, upgrading a bathroom in Woodward Park, adding a room in Fig Garden, finishing a basement in Clovis, or completing a whole-home renovation in Sunnyside, our licensed C-10 electricians provide complete electrical services for your remodel project. Home renovations often require new circuits, panel upgrades, rewiring, lighting installations, and code compliance updates. We work closely with contractors, designers, and homeowners to ensure electrical work is completed on schedule, meets California Title 24 standards, and supports your renovation vision."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'Do I need an electrician for my Fresno home renovation?',
            answer:
              'Yes, any renovation involving electrical work requires a licensed electrician and proper permits in Fresno. This includes adding outlets or switches, installing new lighting, upgrading circuits, moving electrical panels, or any work that involves opening walls and accessing wiring. California law requires electrical work be performed by licensed C-10 contractors and inspected for code compliance.',
          },
          {
            question: 'How much does renovation electrical work cost in Fresno?',
            answer:
              'Costs vary widely by project scope. Simple bathroom electrical upgrades typically cost $800–$2,000. Kitchen remodel electrical work ranges from $2,500–$8,000. Room addition electrical typically costs $3,000–$6,000. Basement finishing electrical ranges from $2,000–$5,000. Whole-home renovation electrical can cost $10,000–$30,000+ depending on home size and scope. We provide free detailed quotes for all Fresno renovation electrical projects.',
          },
          {
            question: 'Will my electrical panel need upgrading during renovation?',
            answer:
              "Many Fresno home renovations require electrical panel upgrades, especially if you're adding major appliances, HVAC systems, or expanding your home. Older homes with 100-amp panels often cannot safely handle modern electrical loads plus renovation additions. If your renovation includes kitchen appliances, additional HVAC, EV chargers, or significant square footage expansion, a panel upgrade to 200 amps is typically recommended.",
          },
          {
            question: 'How long does renovation electrical work take in Fresno?',
            answer:
              'Timeline depends on project scope. Simple bathroom electrical upgrades take 1-2 days. Kitchen remodel electrical typically takes 3-5 days. Room addition electrical takes 3-7 days. Basement finishing electrical takes 3-5 days. Whole-home renovation electrical can take 1-3 weeks. We coordinate closely with your contractor and renovation schedule to minimize delays.',
          },
          {
            question: 'Do you coordinate with other contractors during renovations?',
            answer:
              'Absolutely. We work closely with general contractors, designers, plumbers, HVAC contractors, and other trades throughout your Fresno renovation project. Electrical work must be coordinated with framing, drywall, plumbing, and HVAC installation to ensure proper sequencing and avoid conflicts.',
          },
          {
            question: 'Will renovation electrical work require permits and inspections?',
            answer:
              'Yes, most renovation electrical work in Fresno requires permits from the local building department and inspections by city or county inspectors. We handle all permit applications, schedule required inspections, and ensure your renovation electrical work passes inspection and meets California Title 24 codes.',
          },
        ]}
        serviceSlug="home-renovation-electrical"
      />
    </>
  );
}
